<template>
  <v-card class="sale-success-card" flat>
    <div class="sale-success-card__status">
      <div class="sale-success-card__status-icon">
        <v-icon color="white" size="28">mdi-check-bold</v-icon>
      </div>
    </div>
    <div class="sale-success-card__headline">
      Venta #{{ saleFolioDisplay }} exitosa.
    </div>
    <v-window v-model="windowModel">
      <v-window-item :value="0">
        <div class="sale-success-card__totals">
          <div class="d-flex justify-space-between">
            <span>Total de la Venta:</span>
            <strong>{{ total }}</strong>
          </div>
          <div v-if="cashChange" class="d-flex justify-space-between align-center">
            <span>Cambio:</span>
            <strong class="sale-success-card__totals-change">{{ cashChange }}</strong>
          </div>
        </div>
        <div v-if="invoiceErrorMessage" class="sale-success-card__invoice-warning">
          <div class="sale-success-card__invoice-warning-title">
            La venta se completó, pero la factura no pudo generarse.
          </div>
          <div class="sale-success-card__invoice-warning-message">
            {{ invoiceErrorMessage }}
          </div>
          <div class="sale-success-card__invoice-warning-help">
            Si tienes problemas con el timbrado, realiza la factura en el módulo de
            <strong>Administrador &gt; Facturación</strong>.
          </div>
        </div>
        <div class="sale-success-card__section d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon small color="grey darken-1" class="mr-2">mdi-printer-pos</v-icon>
            <span class="sale-success-card__text">Ticket impreso en térmica</span>
          </div>
          <v-btn
            outlined
            depressed
            color="primary"
            class="sale-success-card__btn sale-success-card__btn--small"
            :loading="printingTicket"
            @click="$emit('reprint-ticket')"
          >
            Reimprimir ticket
          </v-btn>
        </div>

        <div class="sale-success-card__section">
          <div class="sale-success-card__section-title">
            Documentos disponibles (descarga)
          </div>

          <!-- Check 1: Ticket PDF -->
          <v-checkbox
            v-model="selectedDocs"
            value="ticket_pdf"
            label="Ticket de venta (PDF)"
            dense
            hide-details
            class="sale-success-card__check"
            color="primary"
          />

          <!-- Check 2: Vale de entrega posterior (si aplica) -->
          <div v-if="showCollectionTicket" class="d-flex align-center justify-space-between">
            <v-checkbox
              v-model="selectedDocs"
              value="delivery_voucher"
              label="📦 Vale de entrega posterior (PDF)"
              dense
              hide-details
              class="sale-success-card__check"
              color="primary"
            />
            <v-btn
              outlined
              depressed
              color="primary"
              class="sale-success-card__btn sale-success-card__btn--small"
              :disabled="!canOpenCollectionTicket"
              @click="openCollectionTicketPreview"
            >
              <v-icon left small>mdi-printer</v-icon>
              Imprimir vale ↗
            </v-btn>
          </div>

          <!-- Check 3 y 4: Factura PDF / XML (si aplica) -->
          <template v-if="invoiceResponse">
            <v-checkbox
              v-model="selectedDocs"
              value="invoice_pdf"
              label="Factura fiscal (PDF)"
              dense
              hide-details
              class="sale-success-card__check"
              color="primary"
            />
            <v-checkbox
              v-model="selectedDocs"
              value="invoice_xml"
              label="Factura XML (CFDI)"
              dense
              hide-details
              class="sale-success-card__check"
              color="primary"
            />
          </template>

          <!-- Botón de Descarga por Lote -->
          <v-btn
            block
            depressed
            color="light-blue lighten-4"
            class="sale-success-card__btn primary--text mt-3"
            :disabled="selectedDocs.length === 0 || downloading"
            @click="onDownload"
          >
            <v-icon left small>mdi-download</v-icon>
            Descargar seleccionados (ZIP / {{ selectedDocs.length }} archivos)
          </v-btn>
        </div>
        <v-btn
          block
          depressed
          outlined
          color="primary"
          class="sale-success-card__btn sale-success-card__action"
          @click="showEmailClientWindow"
        >
          <v-icon left small>mdi-email</v-icon>
          Enviar comprobantes de la venta por correo
        </v-btn>
      </v-window-item>
      <v-window-item :value="1">
        <span class="sale-success-card__section-title">Enviar comprobantes de la venta por correo</span>
        <div class="my-4">
          <v-text-field
            v-model="name"
            color="secondary"
            text
            hide-details
            class="mb-4"
            label="Buscar por nombre">
          </v-text-field>
          <div v-if="showSimilarClients()" class="mt-2">
            <h5>Clientes con nombre similar:</h5>
            <span v-for="(item, index) in similarClients" :key="index">
              <v-chip
                color="primary"
                class="ma-2"
                outlined
                @click="onSimilarClientClicked(item)"
              >
                <span>{{ item.name }}</span>
                <span v-if="item.rfc && item.rfc != ''">&nbsp;-&nbsp;{{ item.rfc }}</span>
              </v-chip>
            </span>
          </div>
          <v-divider></v-divider>
          <v-text-field
            v-model="email"
            outlined
            label="Enviar comprobantes digitales a esta dirección de correo">
          </v-text-field>
          <v-text-field v-if="false" label="Teléfono"></v-text-field>
          <v-alert
            :value="emailResponse"
            border="left"
            :color="emailSuccess ? 'green' : 'red'"
            dense
            text
            :type="emailSuccess ? 'success' : 'error'"
          >
            <span v-if="emailSuccess">Correo enviado correctamente</span>
            <span v-else>No se pudo enviar el correo</span>
          </v-alert>
          <div class="d-flex justify-space-between">
            <v-btn
              text
              class="sale-success-card__btn"
              @click="windowModel = 0"
            >Regresar</v-btn>
            <v-btn
              depressed
              color="primary"
              class="sale-success-card__btn"
              @click="sendEmail"
              :rules="[validateEmail]"
              :disabled="!validateEmail(email)"
              :loading="sendingEmail"
            >
              Enviar
            </v-btn>
          </div>
        </div>
      </v-window-item>
    </v-window>
    <div class="d-flex justify-center">
      <v-btn
        depressed
        color="primary"
        class="sale-success-card__btn sale-success-card__btn--large"
        @click="closeDialog"
      >
        Listo - Nueva Venta
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ClientService from '../../services/ClientService';
import PurchaseOrderService from '../../services/PurchaseOrderService';
import { FormUtils } from '../../utils/FormUtils';

export default {
  name: 'sale-success-actions',
  props: {
    saleId: {
      type: [Number, String],
      default: null,
    },
    saleFolio: {
      type: [Number, String],
      default: null,
    },
    clientEmail: {
      type: String,
      default: '',
    },
    total: {
      type: String,
      default: null,
    },
    cashChange: {
      type: String,
      default: null,
    },
    showCollectionTicket: {
      type: Boolean,
      default: false,
    },
    printingTicket: {
      type: Boolean,
      default: false,
    },
    invoiceResponse: {
      type: Object,
      default: null,
    },
    invoiceErrorMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      name: null,
      email: null,
      windowModel: 0,
      nameLookupTimeout: null,
      similarClients: [],
      hasManySimilarClients: false,
      emailResponse: false,
      emailSuccess: false,
      sendingEmail: false,
      downloading: false,
      selectedDocs: [],
    };
  },
  computed: {
    disableSendEmail() {
      return !this.email;
    },
    saleFolioDisplay() {
      return this.saleFolio == null || this.saleFolio === '' ? '----' : this.saleFolio;
    },
    canOpenCollectionTicket() {
      return this.saleFolio != null && this.saleFolio !== '';
    },
  },
  watch: {
    name() {
      this.delayedNameLookup();
    },
  },
  methods: {
    openCollectionTicketPreview() {
      if (!this.canOpenCollectionTicket) return;

      const routeData = this.$router.resolve({
        name: 'resguardo-vale-ticket',
        params: { folio: String(this.saleFolio), mode: 'todas' },
      });

      const openedTab = window.open(routeData.href, '_blank');

      if (!openedTab) {
        this.$router.push({
          name: 'resguardo-vale-ticket',
          params: { folio: String(this.saleFolio) },
        });
      }
    },
    showEmailClientWindow() {
      this.windowModel = 1;
    },
    delayedNameLookup() {
      clearTimeout(this.nameLookupTimeout);
      this.nameLookupTimeout = setTimeout(() => {
        if (this.name && this.name.length > 3) {
          ClientService.getSimilarClients(this.name).then((data) => {
            this.similarClients = data;
          });
        } else {
          this.similarClients = [];
        }
      }, 500);
    },
    showSimilarClients() {
      const hasResults = this.similarClients.length > 0;
      if (!hasResults) {
        return false;
      }
      const hasManyResults = this.similarClients.length > 5;
      if (hasManyResults) {
        this.hasManySimilarClients = true;
        return false;
      }
      this.hasManySimilarClients = false;
      return true;
    },
    onSimilarClientClicked(client) {
      if (client.contactInfo && client.contactInfo.length > 0) {
        for (let i = 0; i < client.contactInfo.length; i += 1) {
          const contact = client.contactInfo[i];
          if (contact.email) {
            this.email = contact.email;
            break;
          }
        }
      }
    },
    trimMax(value, max = 100) {
      return FormUtils.trimMax(value, max);
    },
    validateEmail(value) {
      const email = this.trimMax(value, 100);
      if (!email) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return false;
      }
      return true;
    },
    sendEmail() {
      this.sendingEmail = true;
      PurchaseOrderService.sendEmail(this.saleId, this.saleFolio, this.email).then((response) => {
        this.sendingEmail = false;
        this.emailResponse = true;
        if (response.error) {
          this.emailSuccess = false;
          console.error(response.error);
        } else {
          this.emailSuccess = true;
          this.name = '';
          this.email = '';
          this.similarClients = [];
        }
      });
    },
    downloadBlob(response, fallbackName = 'download.zip') {
      const disposition = response.headers['content-disposition'] || '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1]) : fallbackName;

      const url = URL.createObjectURL(
        new Blob([response.data], { type: 'application/zip' }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    async onDownload() {
      this.downloading = true;
      try {
        const res = await PurchaseOrderService.downloadDocuments(this.saleFolio, this.selectedDocs);
        this.downloadBlob(res, `documentos-${this.saleFolio}.zip`);
      } catch (e) {
        const text = e.response?.data instanceof Blob ? await e.response.data.text() : null;
        console.log(text ?? e);
      } finally {
        this.downloading = false;
      }
    },
    closeDialog() {
      this.emailSuccess = false;
      this.emailResponse = false;
      this.email = '';
      this.name = '';
      this.similarClients = [];
      this.hasManySimilarClients = false;
      this.sendingEmail = false;
      this.windowModel = 0;
      this.$emit('close');
    },
  },
  mounted() {
    this.email = this.clientEmail;
    this.selectedDocs.push('ticket_pdf');
    if (this.invoiceResponse) {
      this.selectedDocs.push('invoice_pdf');
      this.selectedDocs.push('invoice_xml');
    }
    if (this.showCollectionTicket) {
      this.selectedDocs.push('delivery_voucher');
    }
  },
};
</script>

<style lang="less" scoped>
.sale-success-card {
  border: 2px solid #8f96a3;
  border-radius: 24px;
  padding: 40px 28px 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f8fc 100%);
  box-shadow: 0 18px 40px rgba(23, 32, 63, 0.08);
}

.sale-success-card__status {
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
}

.sale-success-card__status-icon {
  width: 54px;
  height: 54px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #8ceb1b 0%, #61da14 100%);
  box-shadow: 0 10px 24px rgba(100, 221, 23, 0.35);
}

.sale-success-card__headline {
  margin-bottom: 22px;
  text-align: center;
  color: #2d3656;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}

.sale-success-card {
  font-family: inherit;
}

.sale-success-card__totals {
  font-size: 20px;
  margin-bottom: 16px;
  padding: 0 4px;
  line-height: 1.5;
}

.sale-success-card__totals-change {
  color: #30ad4b;
}

.sale-success-card__section {
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 14px;
  background: #f1f3f8;
}

.sale-success-card__section-title {
  margin-bottom: 8px;
  color: #4a5270;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.sale-success-card__text {
  color: #2d3656;
  font-size: 14px;
  font-weight: 500;
}

.sale-success-card__check {
  margin-top: 4px;
  margin-bottom: 4px;

  /deep/ .v-label {
    color: #2d3656;
    font-size: 14px;
  }
}

.sale-success-card__btn {
  height: 40px !important;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.sale-success-card__btn--small {
  height: 32px !important;
  font-size: 13px;
}

.sale-success-card__btn--large {
  height: 48px !important;
  min-width: 220px !important;
  font-size: 16px;
}

.sale-success-card__invoice-warning {
  margin-bottom: 22px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff8e6;
  border: 1px solid #f5d08a;
  color: #9a5b00;
}

.sale-success-card__invoice-warning-title {
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
}

.sale-success-card__invoice-warning-message {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
}

.sale-success-card__invoice-warning-help {
  font-size: 14px;
  line-height: 1.4;
}

.sale-success-card__action {
  margin-bottom: 16px;
}

@media (max-width: 600px) {
  .sale-success-card {
    padding: 28px 18px 20px;
    border-radius: 18px;
  }

  .sale-success-card__headline {
    font-size: 22px;
  }
}
</style>
